/**
 * FAQ Storefront Loader
 * Loads FAQ data from Shopify Storefront API and renders to page
 * Compatible with ProductFAQAccordion accordion functionality
 */

class FAQStorefrontLoader {
  constructor(config) {
    this.storeDomain = config.storeDomain || window.Shopify.shop;
    this.accessToken = config.accessToken;
    this.apiVersion = config.apiVersion || '2024-10';
    this.productHandle = config.productHandle;
    this.containerId = config.containerId || 'product-faq-inline';

    if (!this.accessToken) {
      console.error('FAQStorefrontLoader: Access token is required');
      return;
    }

    this.init();
  }

  async init() {
    try {
      const faqs = await this.fetchFAQs();
      if (faqs && faqs.length > 0) {
        this.render(faqs);
        this.initializeAccordion();
      } else {
        this.hideContainer();
      }
    } catch (error) {
      console.error('FAQStorefrontLoader error:', error);
      this.hideContainer();
    }
  }

  async fetchFAQs() {
    const query = `
      query getProductFAQs($productHandle: String!) {
        product(handle: $productHandle) {
          id
          metafield(namespace: "custom", key: "product_faqs") {
            id
            value
          }
        }
      }
    `;

    const endpoint = `https://${this.storeDomain}/api/${this.apiVersion}/graphql.json`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.accessToken
      },
      body: JSON.stringify({
        query,
        variables: { productHandle: this.productHandle }
      })
    });

    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status}`);
    }

    const { data, errors } = await response.json();

    if (errors) {
      console.error('GraphQL errors:', errors);
      return null;
    }

    return this.parseFAQs(data);
  }

  parseFAQs(data) {
    if (!data?.product?.metafield?.value) {
      return null;
    }

    try {
      const faqs = JSON.parse(data.product.metafield.value);
      return Array.isArray(faqs) ? faqs.filter(faq => faq.question && faq.answer) : null;
    } catch (error) {
      console.error('Failed to parse FAQ JSON:', error);
      return null;
    }
  }

  render(faqs) {
    const container = document.querySelector(`#${this.containerId}`);

    if (!container) {
      console.warn(`FAQ container #${this.containerId} not found`);
      return;
    }

    // Show the container
    container.style.display = 'block';

    // Find or create the FAQ list container
    let listContainer = container.querySelector('.product-faq__list');

    if (!listContainer) {
      console.warn('FAQ list container not found');
      return;
    }

    // Clear existing content (loading placeholders)
    listContainer.innerHTML = '';

    // Render FAQs with Schema.org markup
    faqs.forEach((faq, index) => {
      const faqItem = this.createFAQElement(faq, index + 1);
      listContainer.appendChild(faqItem);
    });

    // Add Schema.org FAQPage wrapper if not exists
    if (!listContainer.hasAttribute('itemscope')) {
      listContainer.setAttribute('itemscope', '');
      listContainer.setAttribute('itemtype', 'https://schema.org/FAQPage');
    }
  }

  createFAQElement(faq, index) {
    const faqItem = document.createElement('div');
    faqItem.className = 'faq-item';
    faqItem.setAttribute('data-faq-item', '');
    faqItem.setAttribute('itemscope', '');
    faqItem.setAttribute('itemprop', 'mainEntity');
    faqItem.setAttribute('itemtype', 'https://schema.org/Question');

    faqItem.innerHTML = `
      <button
        class="faq-item__question"
        id="faq-question-specs-${index}"
        aria-expanded="false"
        aria-controls="faq-answer-specs-${index}"
        data-faq-trigger
        type="button"
        itemprop="name"
      >
        <span class="faq-item__question-text">
          ${this.escapeHtml(faq.question)}
        </span>
        <span class="faq-item__icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </button>

      <div
        class="faq-item__answer"
        id="faq-answer-specs-${index}"
        role="region"
        aria-labelledby="faq-question-specs-${index}"
        hidden
        itemscope
        itemprop="acceptedAnswer"
        itemtype="https://schema.org/Answer"
      >
        <div class="faq-item__answer-content" itemprop="text">
          ${faq.answer}
        </div>
      </div>
    `;

    return faqItem;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  hideContainer() {
    const container = document.querySelector(`#${this.containerId}`);
    if (container) {
      container.style.display = 'none';
    }
  }

  initializeAccordion() {
    // Trigger the existing ProductFAQAccordion initialization
    const container = document.querySelector(`#${this.containerId}`);

    if (container && typeof ProductFAQAccordion !== 'undefined') {
      new ProductFAQAccordion(container);
    } else {
      // Fallback: basic accordion functionality
      this.initBasicAccordion();
    }
  }

  initBasicAccordion() {
    const triggers = document.querySelectorAll(`#${this.containerId} [data-faq-trigger]`);

    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        const answerId = trigger.getAttribute('aria-controls');
        const answer = document.getElementById(answerId);

        if (answer) {
          trigger.setAttribute('aria-expanded', !isExpanded);
          answer.hidden = isExpanded;
        }
      });
    });
  }
}

// Export for module usage or global access
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FAQStorefrontLoader;
} else {
  window.FAQStorefrontLoader = FAQStorefrontLoader;
}
